import { useState } from 'react'
import { downloadFile } from '../api/client'
import { useHandoverPreview } from '../api/queries'
import { Card, ErrorBox } from './ui'

/**
 * Afleveringsmappen på sagen (beslutning 056).
 *
 * Kortet siger, hvad mappen kommer til at indeholde, før man trykker - dels så
 * man ved, hvad bygherren får, dels fordi fotoene hentes fra SharePoint, når
 * mappen bygges, og det tager et øjeblik på en sag med mange billeder.
 */
export function HandoverCard({ projectId, projectName }: { projectId: number; projectName: string }) {
  const { data, isLoading } = useHandoverPreview(projectId)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function download() {
    setError(null)
    setBusy(true)
    try {
      await downloadFile(
        `/api/projects/${projectId}/handover`,
        `Afleveringsmappe - ${projectName}.pdf`
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Mappen kunne ikke hentes.')
    } finally {
      setBusy(false)
    }
  }

  if (isLoading || !data) return null

  const parts: string[] = []
  if (data.tasks > 0) parts.push(`${data.tasks} ${data.tasks === 1 ? 'opgave' : 'opgaver'}`)
  if (data.photosIncluded > 0) parts.push(`${data.photosIncluded} fotos`)
  if (data.documents > 0) {
    parts.push(`${data.documents} ${data.documents === 1 ? 'dokument' : 'dokumenter'} på listen`)
  }

  return (
    <Card title="Afleveringsmappe">
      <p className="small muted" style={{ marginTop: 0 }}>
        Hele sagens dokumentation i ét dokument til bygherren: sagens oplysninger, det udførte
        arbejde, fejl- og mangellisten og fotoene.
      </p>

      {parts.length > 0 ? (
        <p className="small" style={{ marginTop: 0 }}>
          Mappen kommer til at indeholde <strong>{parts.join(', ')}</strong>.
        </p>
      ) : (
        <p className="small" style={{ marginTop: 0 }}>
          Der står hverken opgaver eller filer på sagen endnu, så mappen bliver kun til
          sagens oplysninger.
        </p>
      )}

      {data.defectsModuleEnabled && data.openDefects > 0 && (
        <p className="small" style={{ marginTop: 0, color: '#B45309' }}>
          <strong>
            {data.openDefects} {data.openDefects === 1 ? 'punkt' : 'punkter'} på fejl- og
            mangellisten står åbne
          </strong>{' '}
          og kommer med i mappen. Det er med vilje: bygherren skal kunne se, hvad der udestår.
        </p>
      )}

      {data.photos > data.photosIncluded && (
        <p className="small" style={{ marginTop: 0, color: '#B45309' }}>
          Sagen har {data.photos} fotos. Kun de {data.maxPhotos} nyeste kommer med - ellers bliver
          filen for stor til at sende. Mappen siger det selv.
        </p>
      )}

      {!data.filesModuleEnabled && (
        <p className="small muted" style={{ marginTop: 0 }}>
          Filmodulet er slået fra, så mappen bygges uden fotos.
        </p>
      )}

      {error && <ErrorBox error={error} />}

      <button className="btn btn--primary btn--sm" onClick={() => void download()} disabled={busy}>
        {busy ? 'Henter fotoene ...' : 'Hent afleveringsmappe'}
      </button>

      {data.photosIncluded > 0 && (
        <p className="tiny muted" style={{ marginBottom: 0, marginTop: 8 }}>
          Fotoene hentes fra sagens mappe, når du trykker - det tager et øjeblik.
        </p>
      )}
    </Card>
  )
}
